import router from '../router';

// Base API URL
const API_BASE_URL = 'https://api.dale365.com/api/v1';

/**
 * API service that handles all API requests and centralizes error handling
 * Specifically handles 403 Forbidden errors for IP blocking
 */
const apiService = {
  /**
   * Gets the token from localStorage
   * @returns {string|null} The authentication token or null if not logged in
   */
  getToken() {
    return localStorage.getItem('userToken');
  },

  /**
   * Builds headers for API requests, including authorization if token exists
   * @param {Object} additionalHeaders - Additional headers to include
   * @returns {Object} Headers object for fetch request
   */
  getHeaders(additionalHeaders = {}) {
    const headers = {
      'Content-Type': 'application/json',
      ...additionalHeaders
    };

    const token = this.getToken();
    if (token) {
      headers['Authorization'] = `Token ${token}`;
    }

    return headers;
  },

  /**
   * Handles 403 Forbidden errors from the API, managing IP blocking
   * @param {Response} response - The fetch response object
   */
  async handleAccessDenied(response) {
    if (response.status === 403) {
      try {
        // Clone response to read body without consuming the original
        const clonedResponse = response.clone();
        const text = await clonedResponse.text();
        let responseData;
        
        try {
          responseData = JSON.parse(text);
        } catch (e) {
          // Se não for JSON válido, não é nossa resposta específica
          return false;
        }
        
        // Verificar se corresponde EXATAMENTE ao formato {"detail": "Access denied."}
        if (responseData && responseData.detail === 'Access denied.') {
          console.warn('IP BLOQUEADO: Access denied detectado');
          
          // Limpar TODAS as credenciais de autenticação
          localStorage.removeItem('userToken');
          localStorage.removeItem('userLoggedIn');
          localStorage.removeItem('username');
          localStorage.removeItem('botPermissions');
          
          // Limpar cookies
          document.cookie.split(';').forEach(cookie => {
            const [name] = cookie.trim().split('=');
            document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
          });
          
          // Definir flag de IP bloqueado
          localStorage.setItem('ipBlocked', 'true');
          
          // Forçar redirecionamento para página de bloqueio
          window.location.href = '/blocked';
          return true;
        }
      } catch (e) {
        console.error('Erro ao processar resposta de 403:', e);
      }
    }
    return false;
  },

  /**
   * Performs a fetch request with automatic handling of IP blocking (403 errors)
   * @param {string} url - The URL to fetch from
   * @param {Object} options - Fetch options object
   * @returns {Promise} The fetch promise
   */
  async fetchWithErrorHandling(url, options = {}) {
    // Verificar se o IP já está bloqueado antes de fazer qualquer requisição
    if (localStorage.getItem('ipBlocked') === 'true') {
      console.warn('Tentativa de requisição com IP bloqueado:', url);
      // Forçar redirecionamento para página de bloqueio
      window.location.href = '/blocked';
      // Simular erro de rede para falhar a requisição
      throw new Error('Network error: IP blocked');
    }
    
    try {
      const response = await fetch(url, options);
      
      // Handle 403 Forbidden (Access denied) error for IP blocking
      if (await this.handleAccessDenied(response)) {
        throw new Error('IP blocked');
      }
      
      return response;
    } catch (error) {
      // Check if error has a response object (probably from fetch)
      if (error.response && await this.handleAccessDenied(error.response)) {
        throw new Error('IP blocked');
      }
      throw error;
    }
  },

  /**
   * Performs a GET request to the API
   * @param {string} endpoint - API endpoint
   * @param {Object} additionalHeaders - Additional headers to include
   * @returns {Promise} The fetch promise
   */
  async get(endpoint, additionalHeaders = {}) {
    const url = `${API_BASE_URL}${endpoint}`;
    const options = {
      method: 'GET',
      headers: this.getHeaders(additionalHeaders)
    };
    
    return this.fetchWithErrorHandling(url, options);
  },

  /**
   * Performs a POST request to the API
   * @param {string} endpoint - API endpoint
   * @param {Object} data - Data to send
   * @param {Object} additionalHeaders - Additional headers to include
   * @returns {Promise} The fetch promise
   */
  async post(endpoint, data, additionalHeaders = {}) {
    const url = `${API_BASE_URL}${endpoint}`;
    const options = {
      method: 'POST',
      headers: this.getHeaders(additionalHeaders),
      body: JSON.stringify(data)
    };
    
    return this.fetchWithErrorHandling(url, options);
  },

  /**
   * Performs a PUT request to the API
   * @param {string} endpoint - API endpoint
   * @param {Object} data - Data to send
   * @param {Object} additionalHeaders - Additional headers to include
   * @returns {Promise} The fetch promise
   */
  async put(endpoint, data, additionalHeaders = {}) {
    const url = `${API_BASE_URL}${endpoint}`;
    const options = {
      method: 'PUT',
      headers: this.getHeaders(additionalHeaders),
      body: JSON.stringify(data)
    };
    
    return this.fetchWithErrorHandling(url, options);
  },

  /**
   * Performs a DELETE request to the API
   * @param {string} endpoint - API endpoint
   * @param {Object} additionalHeaders - Additional headers to include
   * @returns {Promise} The fetch promise
   */
  async delete(endpoint, additionalHeaders = {}) {
    const url = `${API_BASE_URL}${endpoint}`;
    const options = {
      method: 'DELETE',
      headers: this.getHeaders(additionalHeaders)
    };
    
    return this.fetchWithErrorHandling(url, options);
  },

  /**
   * Performs login with username and password
   * @param {string} username - User's username
   * @param {string} password - User's password
   * @returns {Promise} The fetch promise
   */
  async login(username, password) {
    const url = `${API_BASE_URL}/login/`;
    const options = {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ username, password })
    };
    
    return this.fetchWithErrorHandling(url, options);
  }
};

export default apiService;
